import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useHeroParallax } from '@/hooks/useParallax';

export default function Home() {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState<Record<string, boolean>>({});
  const { heroRef, scrollY } = useHeroParallax();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoMuted, setIsVideoMuted] = useState(true);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Browsers reliably allow autoplay when video starts muted.
    video.muted = true;
    video.defaultMuted = true;

    let gestureCleanup: (() => void) | null = null;

    const tryPlay = () => {
      video.play().catch(() => {
        if (gestureCleanup) return;

        const onUserGesture = () => {
          video.play().catch(() => {});
          if (gestureCleanup) {
            gestureCleanup();
            gestureCleanup = null;
          }
        };

        const events = ['click', 'touchstart', 'keydown'] as const;
        events.forEach((evt) => document.addEventListener(evt, onUserGesture, { once: true }));

        gestureCleanup = () => {
          events.forEach((evt) => document.removeEventListener(evt, onUserGesture));
        };
      });
    };

    if (video.readyState >= 2) {
      tryPlay();
    } else {
      video.addEventListener('canplay', tryPlay, { once: true });
      video.addEventListener('loadeddata', tryPlay, { once: true });
    }

    return () => {
      if (gestureCleanup) {
        gestureCleanup();
        gestureCleanup = null;
      }
      video.pause();
    };
  }, []);

  const stats = [
    { number: 'Desde 1988', label: '', icon: 'ri-calendar-line' },
    { number: '100%', label: 'Aprobación en pruebas nacionales', icon: 'ri-graduation-cap-line' },
    { number: '3', label: t('stats.languages'), icon: 'ri-global-line' },
  ];

  const benefits = [
    { icon: 'ri-book-open-line', title: t('benefits.academic'), desc: t('benefits.academicDesc') },
    { icon: 'ri-translate-2', title: t('benefits.bilingual'), desc: t('benefits.bilingualDesc') },
    { icon: 'ri-computer-line', title: t('benefits.innovation'), desc: t('benefits.innovationDesc') },
    { icon: 'ri-football-line', title: t('benefits.sports'), desc: t('benefits.sportsDesc') },
    { icon: 'ri-palette-line', title: t('benefits.arts'), desc: t('benefits.artsDesc') },
    { icon: 'ri-heart-line', title: t('benefits.values'), desc: t('benefits.valuesDesc') },
  ];

  const programs = [
    {
      title: t('programs.preschool'),
      desc: t('programs.preschoolDesc'),
      icon: 'ri-bear-smile-line',
      image: 'https://storage.readdy-site.link/project_files/2dbf3bce-c893-400a-8ef5-86be21e9463b/76e7e412-54d7-42c5-9d86-269a911a4db8_compressed_PreescolarF_comprimida.webp',
    },
    {
      title: t('programs.primary'),
      desc: t('programs.primaryDesc'),
      icon: 'ri-book-2-line',
      image: 'https://storage.readdy-site.link/project_files/2dbf3bce-c893-400a-8ef5-86be21e9463b/f598c90d-458d-489c-a92a-ba54801e0155_compressed_PrimariaF_comprimida.webp',
    },
    {
      title: t('programs.secondary'),
      desc: t('programs.secondaryDesc'),
      icon: 'ri-school-line',
      image: 'https://storage.readdy-site.link/project_files/2dbf3bce-c893-400a-8ef5-86be21e9463b/30cdd37c-93dd-429b-9997-6a6735c27013_compressed_SecundariaMini.webp',
    },
  ];

  const galleryImages = [
    'https://storage.readdy-site.link/project_files/2dbf3bce-c893-400a-8ef5-86be21e9463b/46cacee2-1ca0-4f2b-b47b-8e12084cf87b_compressed_Colegio2.webp',
    'https://storage.readdy-site.link/project_files/2dbf3bce-c893-400a-8ef5-86be21e9463b/db90ed64-19f4-40f7-9c68-a558aa53087e_compressed_Ciencia3.webp',
    'https://storage.readdy-site.link/project_files/2dbf3bce-c893-400a-8ef5-86be21e9463b/d8376981-b6cd-42c4-9afd-f43df8bf6782_compressed_Bastquet21.webp',
    'https://storage.readdy-site.link/project_files/2dbf3bce-c893-400a-8ef5-86be21e9463b/467036e9-55d8-4ac5-86f1-bdd245773a4e_compressed_Preescolar21.webp',
    'https://storage.readdy-site.link/project_files/2dbf3bce-c893-400a-8ef5-86be21e9463b/77efb7a2-9be6-420b-8ecc-937b66322eba_compressed_Voleyball31.webp',
    'https://storage.readdy-site.link/project_files/2dbf3bce-c893-400a-8ef5-86be21e9463b/2330bf53-cc44-4a64-995a-7875fc425944_compressed_Gen2026.webp',
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section with Parallax */}
      <section
        ref={heroRef}
        className="relative h-[600px] md:h-[800px] flex items-center justify-center overflow-hidden"
      >
        {/* Parallax Background Video - slowest */}
        <div
          className="absolute inset-0 will-change-transform"
          style={{ transform: `translateY(${scrollY * 0.3}px)` }}
        >
          <video
            ref={videoRef}
            autoPlay
            loop
            muted={isVideoMuted}
            playsInline
            preload="auto"
            className="w-full h-[120%] object-cover object-top"
            poster="https://readdy.ai/api/search-image?query=Elegant%20modern%20private%20school%20campus%20aerial%20view%20with%20lush%20tropical%20gardens%2C%20students%20walking%20between%20modern%20buildings%2C%20warm%20golden%20hour%20lighting%20casting%20long%20shadows%2C%20professional%20architectural%20photography%2C%20navy%20blue%20and%20gold%20accents%20on%20buildings%2C%20Costa%20Rica%20educational%20institution%2C%20premium%20international%20school%20atmosphere&width=1600&height=900&seq=hero2&orientation=landscape"
          >
            <source src="https://storage.readdy-site.link/project_files/2dbf3bce-c893-400a-8ef5-86be21e9463b/b36ef0c3-4a4c-4693-926f-d85102a23161_SLS-WEB---HD-1080p.mp4" type="video/mp4" />
          </video>
          <button
            type="button"
            onClick={() => {
              const video = videoRef.current;
              if (!video) return;
              const nextMuted = !video.muted;
              video.muted = nextMuted;
              setIsVideoMuted(nextMuted);
              if (video.paused) video.play().catch(() => {});
            }}
            className="absolute bottom-6 right-6 z-30 w-11 h-11 rounded-full bg-black/40 backdrop-blur-sm border border-white/30 text-white flex items-center justify-center hover:bg-black/55 transition-colors"
            aria-label={isVideoMuted ? 'Activar sonido del video' : 'Silenciar video'}
            title={isVideoMuted ? 'Activar sonido' : 'Silenciar'}
          >
            <i className={isVideoMuted ? 'ri-volume-mute-line text-xl' : 'ri-volume-up-line text-xl'} />
          </button>
        </div>

        {/* Parallax Gradient Overlay - matches background speed */}
        <div
          className="absolute inset-0 bg-gradient-to-b from-purple-900/70 via-purple-900/50 to-purple-900/80 will-change-transform"
          style={{ transform: `translateY(${scrollY * 0.3}px)` }}
        />

        {/* Content Layer - subtle parallax */}
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <span
            className="inline-block px-5 py-2 bg-white/10 backdrop-blur-sm text-yellow-400 text-xs font-semibold tracking-[0.2em] uppercase rounded-full mb-8 border border-white/20 animate-fade-in will-change-transform"
            style={{ transform: `translateY(${scrollY * -0.05}px)` }}
          >
            {t('home.heroBadge')}
          </span>
          <h1
            className="font-serif text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-6 leading-[1.1] animate-fade-in-up will-change-transform"
            style={{ transform: `translateY(${scrollY * -0.08}px)` }}
          >
            {t('hero.title')}
          </h1>
          <p
            className="text-base md:text-xl text-white/80 mb-10 max-w-2xl mx-auto leading-relaxed font-light animate-fade-in-up animation-delay-200 will-change-transform"
            style={{ transform: `translateY(${scrollY * -0.06}px)` }}
          >
            {t('hero.subtitle')}
          </p>
          <div
            className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up animation-delay-400 will-change-transform"
            style={{ transform: `translateY(${scrollY * -0.04}px)` }}
          >
            <Link
              to="/admisiones"
              className="px-8 py-4 bg-yellow-500 text-white font-semibold rounded-lg hover:bg-yellow-600 transition-all duration-300 whitespace-nowrap text-sm tracking-wide"
            >
              {t('hero.ctaInfo')}
            </Link>
            <Link
              to="/contacto"
              className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-lg hover:bg-white/20 transition-all duration-300 whitespace-nowrap text-sm tracking-wide border border-white/30"
            >
              {t('hero.ctaVisit')}
            </Link>
          </div>
        </div>

        {/* Scroll indicator - subtle inverse parallax */}
        <div
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-fade-in animation-delay-600 will-change-transform"
          style={{ transform: `translateY(${scrollY * 0.15}px)` }}
        >
          <span className="text-white/50 text-xs tracking-wider uppercase">{t('home.scrollDown')}</span>
          <div className="w-5 h-8 border-2 border-white/30 rounded-full flex items-start justify-center p-1">
            <div className="w-1 h-2 bg-white/60 rounded-full animate-bounce" />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section id="stats" data-animate className="bg-purple-800 py-14 md:py-20">
        <div className="w-full px-4 md:px-8 lg:px-16 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {stats.map((stat, index) => (
              <div
                key={index}
                className={`text-center transition-all duration-700 ${
                  isVisible['stats'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-teal-500/15 flex items-center justify-center">
                  <i className={`${stat.icon} text-yellow-400 text-xl`} />
                </div>
                <h3 className="font-serif text-3xl md:text-4xl font-bold text-white mb-1">{stat.number}</h3>
                <p className="text-white/60 text-sm font-light">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" data-animate className="py-20 md:py-28 bg-white">
        <div className="w-full px-4 md:px-8 lg:px-16 max-w-7xl mx-auto">
          <div className="text-center mb-16 md:mb-20">
            <span className="text-yellow-500 text-xs font-semibold tracking-[0.2em] uppercase">{t('home.benefitsBadge')}</span>
            <h2 className="font-serif text-3xl md:text-5xl font-bold text-purple-900 mt-4 leading-tight">
              {t('benefits.title')}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className={`group p-8 md:p-10 rounded-2xl border border-purple-100 hover:border-purple-800 hover:bg-purple-800 card-lift cursor-pointer ${
                  isVisible['benefits'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
                style={{ transitionDelay: `${index * 80}ms` }}
              >
                <div className="w-14 h-14 rounded-xl bg-purple-800 group-hover:bg-teal-500 flex items-center justify-center mb-6 transition-colors duration-300">
                  <i className={`${benefit.icon} text-yellow-400 group-hover:text-white text-2xl transition-colors duration-300`} />
                </div>
                <h3 className="font-serif text-xl font-semibold text-purple-900 group-hover:text-white mb-3 transition-colors duration-300">
                  {benefit.title}
                </h3>
                <p className="text-black/50 group-hover:text-white/70 text-sm leading-relaxed transition-colors duration-300">
                  {benefit.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section id="programs" data-animate className="py-20 md:py-28 bg-purple-50">
        <div className="w-full px-4 md:px-8 lg:px-16 max-w-7xl mx-auto">
          <div className="text-center mb-16 md:mb-20">
            <span className="text-yellow-500 text-xs font-semibold tracking-[0.2em] uppercase">{t('home.programsBadge')}</span>
            <h2 className="font-serif text-3xl md:text-5xl font-bold text-purple-900 mt-4 leading-tight">
              {t('programs.title')}
            </h2>
            <p className="text-black/50 mt-5 max-w-2xl mx-auto text-base leading-relaxed">
              {t('programs.subtitle')}
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            {programs.map((program, index) => (
              <div
                key={index}
                className={`group bg-white rounded-2xl overflow-hidden border border-purple-100 card-lift-yellow-accent ${
                  isVisible['programs'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="h-52 img-zoom">
                  <img
                    src={program.image}
                    alt={program.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-7 md:p-8">
                  <div className="w-12 h-12 rounded-xl bg-purple-50 flex items-center justify-center mb-5">
                    <i className={`${program.icon} text-teal-500 text-xl`} />
                  </div>
                  <h3 className="font-serif text-xl font-semibold text-purple-900 mb-3">{program.title}</h3>
                  <p className="text-black/50 text-sm leading-relaxed mb-5">{program.desc}</p>
                  <Link
                    to="/programas"
                    className="inline-flex items-center text-teal-500 font-semibold text-sm hover:text-teal-600 transition-colors group/link"
                  >
                    {t('common.learnMore')}
                    <i className="ri-arrow-right-line ml-1 transition-transform group-hover/link:translate-x-1" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" data-animate className="py-20 md:py-28 bg-purple-50">
        <div className="w-full px-4 md:px-8 lg:px-16 max-w-7xl mx-auto">
          <div className="text-center mb-16 md:mb-20">
            <span className="text-yellow-500 text-xs font-semibold tracking-[0.2em] uppercase">{t('home.galleryBadge')}</span>
            <h2 className="font-serif text-3xl md:text-5xl font-bold text-purple-900 mt-4 leading-tight">
              {t('gallery.title')}
            </h2>
            <p className="text-black/50 mt-4 max-w-2xl mx-auto text-base leading-relaxed">
              {t('gallery.subtitle')}
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {galleryImages.map((img, index) => (
              <div
                key={index}
                className={`relative overflow-hidden rounded-2xl cursor-pointer group img-zoom card-lift ${
                  index === 0 || index === 5 ? 'md:col-span-2 md:row-span-2' : ''
                } ${
                  isVisible['gallery'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
                style={{ transitionDelay: `${index * 80}ms` }}
              >
                <img
                  src={img}
                  alt={`${t('common.galleryImage')} ${index + 1}`}
                  className={`w-full object-cover ${
                    index === 0 || index === 5 ? 'h-44 md:h-full md:min-h-[500px]' : 'h-44 md:h-60'
                  }`}
                />
                <div className="absolute inset-0 bg-purple-900/0 group-hover:bg-purple-900/30 transition-colors duration-500 flex items-center justify-center">
                  <i className="ri-zoom-in-line text-white text-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="cta" data-animate className="py-20 md:py-28 bg-purple-800">
        <div className="w-full px-4 md:px-8 lg:px-16 max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            {t('home.ctaTitle')}
          </h2>
          <p className="text-white/60 text-lg md:text-xl mb-10 max-w-2xl mx-auto font-light leading-relaxed">
            {t('home.ctaSubtitle')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/admisiones"
              className="px-8 py-4 bg-yellow-500 text-white font-semibold rounded-lg hover:bg-yellow-600 transition-all duration-300 whitespace-nowrap text-sm tracking-wide"
            >
              {t('hero.ctaInfo')}
            </Link>
            <Link
              to="/contacto"
              className="px-8 py-4 border border-white/30 text-white font-semibold rounded-lg hover:bg-white/10 transition-all duration-300 whitespace-nowrap text-sm tracking-wide"
            >
              {t('hero.ctaVisit')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}